import { DefaultTheme } from 'styled-components'
import Highcharts from 'highcharts2/highstock'
import { TimeSpan } from '../../utils/type'
import dateService from 'live/services/date'

export const getOptionsFromTemporalWidget = (
    timespan: TimeSpan,
    series: any,
    theme: DefaultTheme
): Highcharts.Options => {
    const { left, right } = getTemporalWidgetLeftRight()

    return {
        chart: {
            type: 'xrange',
            animation: false,
            backgroundColor: 'transparent',
            marginLeft: left,
            marginRight: right
        },
        plotOptions: {
            xrange: {
                animation: false,
                cursor: 'pointer',
                borderColor: theme['dash-background'],
                borderWidth: 3,
                borderRadius: 1,
                pointPadding: 0.2,
                groupPadding: 0,
                grouping: false,
                turboThreshold: 0,
                minPointLength: 1,
                zIndex: 100
            }
        },
        series: [series],
        xAxis: {
            min: timespan.begin,
            max: timespan.end,
            top: -1,
            tickLength: 5,
            offset: -22,
            opposite: true,
            type: 'datetime',
            visible: true,
            endOnTick: false,
            lineWidth: 1,
            alignTicks: true,
            minTickInterval: 3600 * 1000,
            dateTimeLabelFormats: {
                hour: '%I:%M %p'
            }
        },
        yAxis: {
            visible: false
        },
        time: {
            timezoneOffset: new Date().getTimezoneOffset(),
            timezone: dateService.getUserTimezoneOffset()
        },
        title: {
            text: null
        },
        tooltip: {
            enabled: false
        },
        credits: {
            enabled: false
        },
        legend: {
            enabled: false
        }
    }
}

export const getTemporalWidgetDOM = (): Element => {
    return document.getElementsByClassName('highcharts-plot-background')[0]
}

export const getTemporalWidgetLeftRight = (): { left: number; right: number } => {
    const dashboardWidgets = document.getElementsByClassName('widget-content-container')
    const isTheTemporalAxisWidgetHidden = !!Array.from(dashboardWidgets)
        ?.find(widget => widget.innerHTML.includes('temporal-widget'))
        ?.getAttribute('style')
        ?.includes('display: none')

    const isLegendBoxHidden = document.getElementsByClassName('hide-legend')?.length > 0

    const legendBoxWidth = document.getElementsByClassName('highcharts-legend-box')[0]?.getAttribute('width') as string
    const legendBoxWidthFormatted = parseInt(legendBoxWidth)

    const legendItems = document.getElementsByClassName('highcharts-legend-item')
    const isLegendBoxPositionRight = !!Array.from(legendItems)
        ?.slice(1)
        ?.find(item => item)
        ?.getAttribute('transform')
        ?.includes('translate(5,')

    const yAxisLabels = document.getElementsByClassName('highcharts-yaxis-labels')[0]?.getBoundingClientRect()
    const yAxisLabelsWidth = yAxisLabels?.width
    const isYAxisLabelsPositionLeft = yAxisLabels?.x < 100

    let axisTitleWidth = 0
    const axisTitle = document.getElementsByClassName('highcharts-axis-title')
    const isAxisTitleVisible = axisTitle?.length > 0
    if (isAxisTitleVisible) {
        axisTitleWidth = axisTitle[0]?.getBoundingClientRect().height
    }

    let isScrollbarsCustomPositionLeft = false
    let scrollbarsCustomWidth = 0
    const scrollbarsCustom = document.getElementsByClassName('ScrollbarsCustom')
    const isScrollbarsCustomVisible = scrollbarsCustom?.length > 0
    if (isScrollbarsCustomVisible) {
        isScrollbarsCustomPositionLeft = scrollbarsCustom[0]?.getBoundingClientRect().x < 50
        scrollbarsCustomWidth = scrollbarsCustom[0]?.getBoundingClientRect().width
    }

    if (isTheTemporalAxisWidgetHidden) return { left: 8, right: 8 }
    /// // Creating possible widget customization scenarios

    /// // Scenarios with Scrollbars Custom
    // Left
    const scrollbarsCustomOnTheLeft =
        isScrollbarsCustomVisible && isScrollbarsCustomPositionLeft && (isLegendBoxHidden || !isLegendBoxPositionRight) // left: scrollbarsCustomWidth + 13; right: 9

    const scrollbarsCustomOnTheLeftWithLegendBoxOnTheRight =
        isScrollbarsCustomVisible && isScrollbarsCustomPositionLeft && isLegendBoxPositionRight // left: scrollbarsCustomWidth + 13; right: legendBoxWidthFormatted + 36

    // Right
    const scrollbarsCustomOnTheRight =
        isScrollbarsCustomVisible && !isScrollbarsCustomPositionLeft && (isLegendBoxHidden || !isLegendBoxPositionRight) // left: 10; right: scrollbarsCustomWidth + 10

    const scrollbarsCustomOnTheRightWithLegendBoxOnTheRight =
        isScrollbarsCustomVisible && !isScrollbarsCustomPositionLeft && isLegendBoxPositionRight // left: 10; right: scrollbarsCustomWidth + legendBoxWidthFormatted + 32

    /// // Scenarios with yAxisLabels
    // Left
    const yAxisLabelsOnTheLeft =
        isYAxisLabelsPositionLeft && !axisTitleWidth && (isLegendBoxHidden || !isLegendBoxPositionRight) // left: yAxisLabelsWidth + 24; right: 9

    const yAxisLabelsOnTheLeftWithLegendBoxOnTheRight =
        isYAxisLabelsPositionLeft && !axisTitleWidth && isLegendBoxPositionRight // left: yAxisLabelsWidth + 24; right: legendBoxWidthFormatted + 36

    const yAxisLabelsWithAxisTitleOnTheLeft =
        isYAxisLabelsPositionLeft && axisTitleWidth && (isLegendBoxHidden || !isLegendBoxPositionRight) // left: yAxisLabelsWidth + axisTitleWidth + 17; right: 9

    const yAxisLabelsWithAxisTitleOnTheLeftAndWithLegendBoxOnTheRight =
        isYAxisLabelsPositionLeft && axisTitleWidth && isLegendBoxPositionRight // left: yAxisLabelsWidth + axisTitleWidth + 17; right: legendBoxWidthFormatted + 36

    // Right
    const yAxisLabelsOnTheRight =
        !isYAxisLabelsPositionLeft && !axisTitleWidth && (isLegendBoxHidden || !isLegendBoxPositionRight) // left: 9; right: yAxisLabelsWidth + 25

    const yAxisLabelsOnTheRightWithLegendBoxOnTheRight =
        !isYAxisLabelsPositionLeft && !axisTitleWidth && isLegendBoxPositionRight // left: 9; right: yAxisLabelsWidth + legendBoxWidthFormatted + 50

    const yAxisLabelsWithAxisTitleOnTheRight =
        !isYAxisLabelsPositionLeft && axisTitleWidth && (isLegendBoxHidden || !isLegendBoxPositionRight) // left: 9; right: yAxisLabelsWidth + axisTitleWidth + 15

    const yAxisLabelsWithAxisTitleOnTheRightAndWithLegendBoxOnTheRight =
        !isYAxisLabelsPositionLeft && axisTitleWidth && isLegendBoxPositionRight // left: 9; right: yAxisLabelsWidth + legendBoxWidthFormatted + axisTitleWidth + 37

    if (scrollbarsCustomOnTheLeft) {
        return { left: scrollbarsCustomWidth + 13, right: 9 }
    } else if (scrollbarsCustomOnTheLeftWithLegendBoxOnTheRight) {
        return { left: scrollbarsCustomWidth + 13, right: legendBoxWidthFormatted + 36 }
    } else if (scrollbarsCustomOnTheRight) {
        return { left: 10, right: scrollbarsCustomWidth + 10 }
    } else if (scrollbarsCustomOnTheRightWithLegendBoxOnTheRight) {
        return { left: 10, right: scrollbarsCustomWidth + legendBoxWidthFormatted + 32 }
    } else if (yAxisLabelsOnTheLeft) {
        return { left: yAxisLabelsWidth + 24, right: 9 }
    } else if (yAxisLabelsOnTheLeftWithLegendBoxOnTheRight) {
        return { left: yAxisLabelsWidth + 24, right: legendBoxWidthFormatted + 36 }
    } else if (yAxisLabelsWithAxisTitleOnTheLeft) {
        return { left: yAxisLabelsWidth + axisTitleWidth + 17, right: 9 }
    } else if (yAxisLabelsWithAxisTitleOnTheLeftAndWithLegendBoxOnTheRight) {
        return { left: yAxisLabelsWidth + axisTitleWidth + 17, right: legendBoxWidthFormatted + 36 }
    } else if (yAxisLabelsOnTheRight) {
        return { left: 9, right: yAxisLabelsWidth + 25 }
    } else if (yAxisLabelsOnTheRightWithLegendBoxOnTheRight) {
        return { left: 9, right: yAxisLabelsWidth + legendBoxWidthFormatted + 50 }
    } else if (yAxisLabelsWithAxisTitleOnTheRight) {
        return { left: 9, right: yAxisLabelsWidth + axisTitleWidth + 15 }
    } else if (yAxisLabelsWithAxisTitleOnTheRightAndWithLegendBoxOnTheRight) {
        return { left: 9, right: yAxisLabelsWidth + legendBoxWidthFormatted + axisTitleWidth + 37 }
    }

    return { left: 8, right: 8 }
}
